/**
 * shadcn/ui Button, re-skinned onto the brand tokens (foundation pilot).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * - `default` is the house primary: the gold wash, the gold edge, the action ink.
 * - Every size keeps the 44 px touch floor (M5 P5d #4d) except `sm`, which reaches it on
 *   a coarse pointer.
 * - Disabled is a solid quiet border and muted ink, never a bare opacity fade (UX_SPEC 3).
 *   A disabled action states its reason in words: see ActionButton.
 */
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

export const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border text-sm",
    "tracking-[0.015em] transition-[background,border-color,box-shadow,color] duration-200",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
    "disabled:border-rule-strong disabled:bg-none disabled:bg-transparent disabled:text-muted-foreground disabled:shadow-none",
    "aria-disabled:border-rule-strong aria-disabled:bg-none aria-disabled:bg-transparent aria-disabled:text-muted-foreground",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "border-btn-border bg-(image:--q-btn-bg) text-action enabled:hover:border-btn-border-hover enabled:hover:bg-(image:--q-btn-bg-hover) enabled:hover:text-btn-hover-ink enabled:hover:shadow-(--q-btn-glow)",
        outline:
          "border-rule-strong bg-transparent text-foreground enabled:hover:border-edge-hover enabled:hover:bg-accent",
        ghost:
          "border-transparent bg-transparent text-muted-foreground enabled:hover:bg-accent enabled:hover:text-foreground",
        destructive: "border-danger-edge bg-danger-wash text-danger enabled:hover:border-danger",
        link: "border-transparent bg-transparent px-0 text-gold underline-offset-4 enabled:hover:underline",
      },
      size: {
        default: "min-h-11 px-4",
        sm: "min-h-8 px-3 pointer-coarse:min-h-11",
        lg: "min-h-12 px-6 text-base",
        icon: "size-11",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & { readonly asChild?: boolean };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  type = "button",
  ...props
}: ButtonProps): React.JSX.Element {
  if (asChild) {
    return (
      <Slot.Root
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  }
  return (
    <button
      data-slot="button"
      type={type}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}
